import { Show } from 'solid-js'
import heroImg from '~/assets/hero.png'
import { ClockIcon } from '~/components/icons/clock'
import { MonthlyProgressBar } from '~/components/monthly-progress-bar'
import { splitSeconds } from '~/lib/countdown'
import { m } from '~/paraglide/messages'

type Props = {
  current: number
  total: number
  plantName: string
  claimState: 'progress' | 'ready' | 'claimed'
  endSeconds: number
  imageUrl: string
  loading?: boolean
  onClaim: () => void
}

function subtitleFor(claimState: Props['claimState']): string {
  if (claimState === 'ready') return m.monthly_challenge_description_done()
  if (claimState === 'claimed')
    return m.monthly_challenge_description_collected()
  return m.monthly_challenge_description()
}

function resolveBarState(
  claimState: Props['claimState'],
  current: number,
  loading: boolean,
): 'default' | 'in-progress' | 'claim' | 'claimed' | 'loading' {
  if (loading) return 'loading'
  if (claimState === 'ready') return 'claim'
  if (claimState === 'claimed') return 'claimed'
  return current > 0 ? 'in-progress' : 'default'
}

export function Hero(props: Props) {
  return (
    <div class="relative w-full overflow-hidden px-[24px] pb-12 pt-[100px]">
      <Show
        when={!props.loading}
        fallback={
          <div
            aria-hidden="true"
            class="pointer-events-none absolute inset-0 h-full w-full animate-pulse bg-gradient-to-b from-[#0d1f40] via-[#13365f] to-[#1d4e7a]"
          />
        }
      >
        <img
          src={props.imageUrl || heroImg}
          alt=""
          aria-hidden="true"
          class="pointer-events-none absolute inset-0 h-full w-full object-cover"
        />
      </Show>

      <div class="relative mx-auto flex w-full max-w-[640px] items-end justify-between gap-[16px]">
        <div class="flex min-w-[200px] max-w-[300px] flex-1 flex-col gap-3 pr-4">
          <div class="flex w-full flex-col gap-1 text-white">
            <p class="animate-fade-in-up text-[20px] font-semibold leading-[28px] drop-shadow-[0_2px_8px_rgba(0,0,0,0.25)]">
              {m.monthly_challenge_title()}
            </p>
            <Show keyed when={props.claimState}>
              {(state) => (
                <p class="animate-fade-in-up text-[16px] leading-[22px] drop-shadow-[0_1px_4px_rgba(0,0,0,0.18)]">
                  {subtitleFor(state)}
                </p>
              )}
            </Show>
          </div>
          <div class="flex w-full flex-col gap-2">
            <MonthlyProgressBar
              current={props.current}
              total={props.total}
              state={resolveBarState(
                props.claimState,
                props.current,
                props.loading ?? false,
              )}
              onClaim={props.onClaim}
            />
            <span class="inline-flex items-center gap-1 whitespace-nowrap text-[12px] leading-[16px] text-white">
              <ClockIcon class="size-[16px]" />
              {m.monthly_challenge_timer(splitSeconds(props.endSeconds))}
            </span>
          </div>
        </div>

        <div class="flex min-w-[80px] max-w-[200px] flex-1 items-center justify-center rounded-[14px] bg-[rgba(255,255,255,0.34)] px-[8px] py-[4px] backdrop-blur-[5.2px]">
          <Show
            when={!props.loading}
            fallback={
              <span
                aria-hidden="true"
                class="block h-[14px] w-[96px] animate-pulse rounded-[4px] bg-white/40"
              />
            }
          >
            <p class="line-clamp-2 min-w-px flex-1 text-center text-[14px] leading-[20px] text-white [text-shadow:0_0_24.9px_rgba(0,0,0,0.25),0_0_6.1px_#74c8ab] [word-break:break-word]">
              {props.plantName}
            </p>
          </Show>
        </div>
      </div>
    </div>
  )
}
